/**
 * ChestMagicEdenBonusIndicator Component
 *
 * Displays a hoverable "2x POINTS" indicator on the Chest page for Magic Eden $ME stakers
 * Shows campaign details on hover including:
 * - Time remaining
 * - Points progress (X/20,000)
 * - Staker eligibility status
 *
 * Positioned above the chest slider bar, left-aligned
 */

import { useState } from 'react';

import { useMagicEdenCampaign } from '@/hooks/useMagicEdenCampaign';

// Magic Eden brand color
const ME_COLOR = '#E42575';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Campaign ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h left`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }
  return `${minutes}m left`;
}

export function ChestMagicEdenBonusIndicator() {
  const { campaignData, isLoading } = useMagicEdenCampaign();
  const [isHovered, setIsHovered] = useState(false);

  // Only show if user is a Magic Eden staker and hasn't reached the cap
  if (isLoading || !campaignData?.isActive || !campaignData?.isMagicEdenStaker || campaignData?.remainingBonus <= 0) {
    return null;
  }

  const isEligible = campaignData.isEligible && campaignData.remainingBonus > 0;
  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Magic Eden 2x Badge */}
      <div
        className="cursor-pointer transition-all duration-200 flex items-center gap-2 px-3 py-1.5 rounded-full"
        style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          backgroundColor: 'rgba(228, 37, 117, 0.15)',
          border: `1px solid ${ME_COLOR}`,
        }}
      >
        <img
          src="/Magic Eden.png"
          alt="Magic Eden"
          className="w-4 h-4 object-contain"
        />
        <span
          className="text-sm font-bold"
          style={{ color: ME_COLOR, fontFamily: 'Outfit, sans-serif' }}
        >
          $ME 2x
        </span>
      </div>

      {/* Hover Tooltip */}
      {isHovered && (
        <div
          className="absolute top-full left-0 mt-2 w-72 p-4 rounded-xl shadow-2xl z-50 animate-slide-in"
          style={{
            backgroundColor: '#343434',
            border: `2px solid ${ME_COLOR}`,
            boxShadow: `0 0 20px rgba(228, 37, 117, 0.3)`,
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                fill={ME_COLOR}
              />
            </svg>
            <h3
              className="text-base font-bold"
              style={{ color: ME_COLOR, fontFamily: 'Outfit, sans-serif' }}
            >
              Magic Eden $ME Stakers
            </h3>
          </div>

          {/* Content */}
          <div className="space-y-3">
            {isEligible ? (
              <>
                {/* For eligible users */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  You're earning <strong style={{ color: ME_COLOR }}>2x points</strong> on every chest opening!
                </p>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-xs mb-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                    <span>Bonus Progress</span>
                    <span style={{ color: ME_COLOR, fontWeight: 600 }}>{progress} pts</span>
                  </div>
                  <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(228, 37, 117, 0.2)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(campaignData.userBonusEarned / campaignData.maxBonus) * 100}%`,
                        backgroundColor: ME_COLOR,
                      }}
                    />
                  </div>
                </div>

                {/* Time Remaining */}
                <div className="flex justify-between items-center text-xs pt-2" style={{ borderTop: `1px solid rgba(228, 37, 117, 0.3)` }}>
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: ME_COLOR, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* For non-eligible users */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  <strong style={{ color: ME_COLOR }}>$ME stakers</strong> earn 2x points on every chest opening!
                </p>

                {/* Call to Action */}
                <div
                  className="p-3 rounded-lg text-center"
                  style={{
                    background: `linear-gradient(92deg, ${ME_COLOR} -4.02%, #FF6B9D 50.55%, ${ME_COLOR} 103.05%)`,
                  }}
                >
                  <p className="text-xs font-bold" style={{ color: '#FFFFFF', fontFamily: 'Outfit, sans-serif' }}>
                    Stake $ME to earn up to 20,000 bonus points!
                  </p>
                </div>

                {/* Time Remaining */}
                <div className="flex justify-between items-center text-xs pt-2" style={{ borderTop: `1px solid rgba(228, 37, 117, 0.3)` }}>
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Offer ends in</span>
                  <span style={{ color: ME_COLOR, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
